import { events, LISTING_TYPE } from "./constants";
import ga4 from "@/lib/tracking/ga4Core";
import dayjs from "dayjs";
import { PARAM_FILTER } from "@/common/config";

/**
 *
 * @param {string|Event} targetOrEvent
 * @param {Object} extraParams
 */
export const trackGenericCTAClickEvent = (targetOrEvent, extraParams = {}) => {
  const eventName = events.GENERIC_CTA_CLICK;
  const target =
    typeof targetOrEvent === "string"
      ? targetOrEvent
      : targetOrEvent.currentTarget.dataset.eventTarget;
  const eventParams = {
    ...extraParams,
    target,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

export const trackDailyRentStart = () => {
  const eventName = events.DAILY_RENT_START;
  const eventParams = {
    listing_type: LISTING_TYPE.DAILY,
    property_category: "all",
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {string} category
 * @param {LISTING_TYPE} listingType
 */
export const trackOpenSearch = (category, listingType) => {
  const eventName = events.OPEN_SEARCH;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {Object} params
 * @param {string} params.property_category
 * @param {number} params.start_date
 * @param {number} params.end_date
 */
export const trackDailyRentSetDate = (params) => {
  const { property_category, start_date, end_date } = params;
  const eventName = events.DAILY_RENT_SET_DATE;
  const eventParams = {
    property_category,
    start_date: dayjs(start_date).format("DD/MM/YYYY"),
    end_date: dayjs(end_date).format("DD/MM/YYYY"),
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

const filtersToGtagParams = (filters) => {
  const { area, price, city_name, direction_name, district_name } =
    filters || {};
  return {
    city_name,
    direction_name,
    district_name,
    min_price: price?.gte,
    max_price: price?.lte,
    min_area: area?.gte,
    max_area: area?.lte,
  };
};

/**
 *
 * @param {LISTING_TYPE} listingType
 * @param {string} category
 * @param {Object} filters
 */
export const trackApplySearch = (listingType, category, filters, count) => {
  const eventName = events.APPLY_SEARCH;
  const { city_name, direction_name, district_name, ...rest } = filters;
  const relevantFilters = filtersToGtagParams(filters);
  const { target, value } = parseFilters(rest);
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    target,
    value,
    ...relevantFilters,
    count,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

export const trackLocationChange = (listingType, category, value) => {
  const eventName = events.LOCATION_CHANGE;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    value,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

const formatMinMaxFilter = (filter) => {
  const { gte, lte } = filter || {};
  return `${gte || ""}-${lte || ""}`;
};

const parseFilters = (filters = {}) => {
  let targets = [];
  let values = [];

  Object.entries(filters).forEach(([key, value]) => {
    if (PARAM_FILTER[key]) {
      return;
    }
    targets.push(key);
    if (key === "price" || key === "area") {
      values.push(formatMinMaxFilter(value));
    } else {
      values.push(value?.eq || value?.lte || value?.gte);
    }
  });

  return {
    target: targets.length ? targets.join("|") : "",
    value: values.length ? values.join("|") : "",
  };
};

export const trackSetSearchParam = (
  listingType,
  category,
  target,
  newFilter,
  oldFilters,
) => {
  const eventName = events.SET_SEARCH_PARAM;

  const { target: newTarget, value: newValue } = parseFilters({
    [target]: newFilter,
  });
  const { target: oldTarget, value: oldValue } = parseFilters(oldFilters);

  const eventParams = {
    listing_type: listingType,
    property_category: category,
    target: oldTarget ? `${oldTarget}|${target}` : newTarget,
    value: oldValue ? `${oldValue}|${newValue}` : newValue,
    old_value: oldValue || undefined,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {string} source
 * @param {string} journey
 * @param {string} listingType
 * @param {string} category
 */
export const trackShowNumber = (
  source = decodeURI(document?.referrer),
  journey = "call_btn_from_seller_info_section",
  listingType,
  category,
) => {
  const eventName = events.CONTACT_USER;
  const eventParams = {
    method: "show_number",
    source,
    journey_details: journey,
    target: "call_button",
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {string} source
 * @param {string} journey
 * @param {string} listingType
 * @param {string} category
 */
export const trackCallUser = (
  source = decodeURI(document?.referrer),
  journey = "call_btn_from_seller_info_section",
  listingType,
  category,
) => {
  const eventName = events.CONTACT_USER;
  const eventParams = {
    // method: "call",
    source,
    journey_details: journey,
    target: "call_button",
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param source
 * @param journey
 * @param {string} listingType
 * @param {string} category
 */
export const trackWhatsappUser = (
  source = decodeURI(document?.referrer),
  journey = "whatsapp_btn_from_seller_info_section",
  listingType,
  category,
) => {
  const eventName = events.CONTACT_USER;
  const eventParams = {
    method: "whatsapp_init",
    source,
    journey_details: journey,
    target: "whatsapp_button",
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

export const trackVideoStart = ({ listingType, category }) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
  };

  const eventName = events.VIDEO_START;
  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {string} reportInformation
 */
export const trackReportItem = (reportInformation) => {
  const eventName = events.REPORT_ITEM;
  const eventParams = {
    value: reportInformation,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {string} value
 * @param {string} listingType
 * @param {string} category
 */
export const trackShareItem = (value, listingType, category) => {
  const eventName = events.SHARE_ITEM;
  const eventParams = {
    value,
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

export const trackOpenFavedListings = () => {
  trackGenericCTAClickEvent("open_favorites_list");
};

/**
 *
 * @param {LISTING_TYPE} listingType
 * @param {string} category
 * @param {string} source
 */
export const trackCategoryChange = (listingType, category, source) => {
  const eventName = events.CATEGORY_CHANGE;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

/**
 *
 * @param {LISTING_TYPE} listingType
 * @param {string} category
 */
export const trackResetSearch = (listingType, category) => {
  const eventName = events.RESET_SEARCH;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

const RNPL_PDP_JOURNEY = "seeker_rnpl_service_from_pdp";

export const trackViewRNPLApplyBtn = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventName = "view_interaction";
  const eventParams = {
    journey_details: RNPL_PDP_JOURNEY,
    listing_type: listingType,
    property_category: category,
    source,
  };

  ga4.trackUserInteraction(eventName, eventParams);
};

export const trackRNPLApply = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    journey_details: RNPL_PDP_JOURNEY,
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_apply", eventParams);
};

export const trackAdPromotion = ({ listingType, category, source, target }) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent(target, eventParams);
};

export const trackRNPLEnable = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_enable", eventParams);
};

export const trackRNPLDisable = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_disable", eventParams);
};

export const trackRNPLTerms = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    journey_details: RNPL_PDP_JOURNEY,
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_terms", eventParams);
};

export const trackRNPLLearnMore = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    journey_details: RNPL_PDP_JOURNEY,
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_learn_more", eventParams);
};

export const trackRNPLEligibilityCheck = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_check_eligibility", eventParams);
};

export const trackRNPLConfigure = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_configure", eventParams);
};

export const trackRNPLShowSchedule = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_show_schedule", eventParams);
};

export const trackRNPLCheckout = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("rnpl_checkout", eventParams);
};

export const trackRNPLCheckCheckout = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  return;
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    journey_details: RNPL_PDP_JOURNEY,
    source,
  };

  trackGenericCTAClickEvent("rnpl_check_checkout", eventParams);
};

export const trackPdpMapView = ({
  listingType,
  category,
  source = "listing details",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("pdp_listing_map_view", eventParams);
};

export const trackPromotionServices = ({
  listingType,
  category,
  source = "listing_top",
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
  };

  trackGenericCTAClickEvent("promotions-services-description", eventParams);
};

export const trackDisplayPaymentOptions = ({
  listingType,
  category,
  source,
}) => {
  const eventParams = {
    listing_type: listingType,
    property_category: category,
    source,
    journey_details: "display_payment_options_from_pdp",
  };

  trackGenericCTAClickEvent(
    "display_payment_options_dropdown_list",
    eventParams,
  );
};

/**
 * *****************************************
 * ALL EVENTS BELOW ARE NOT YET DEPRECATED *
 * *****************************************
 */

export const trackAddNewListingEvent = ({ source }) => {
  return;
  const eventName = events.ADD_NEW_LISTING;
  trackGA4Event({
    event: eventName,
    source,
  });
};

export const trackOpenUserMenu = ({ isLoggedIn, hasNotifs }) => {
  return;
  const event = events.SHOW_USER_MENU;
  trackGA4Event({
    event,
    loggedIn: isLoggedIn,
    hasNotifs,
  });
};

export const trackCloseUserMenu = () => {
  return;
  const event = events.CLOSE_USER_MENU;
  trackGA4Event({
    event,
  });
};

export const trackOpenNotifs = ({ hasNotifs }) => {
  return;
  const event = events.OPEN_NOTIFS;
  trackGA4Event({
    event,
    hasNotifs,
  });
};

export const trackOpenUserProfile = ({ source }) => {
  return;
  const event = events.OPEN_USER_PROFILE;
  trackGA4Event({
    event,
    source,
  });
};

export const trackOpenRequests = () => {
  return;
  const event = events.OPEN_REQUESTS;
  trackGA4Event({
    event,
  });
};

export const trackToggleFavListings = ({ type }) => {
  return;
  const event = events.TOGGLE_FAV_LISTINGS;
  trackGA4Event({
    event,
    type,
  });
};

export const trackOpenContactUs = ({ source }) => {
  return;
  const event = events.OPEN_CONTACT_US;
  trackGA4Event({
    event,
    source,
  });
};

export const trackListingsList = (ctx, listings, extra = {}) => {
  return;
  const event = events.VIEW_LISTING_LIST;
  const arr = [
    "owner",
    "verified_owner",
    "unverified_owner",
    "direct_from_owners",
    "unique_listing",
  ];
  trackGA4Event({
    event,
    listing_category: ctx.category?.id || 0,
    listing_search_filters: arr.find((key) => ctx.filters[key]?.eq) || "all",
    listing_list_page: ctx.page + 1,
    listing_list_view: ctx.basePath?.includes("map") ? "map" : "list",
    ...extra,
    listings: listings.map((listing) =>
      formatListing4GA({
        ...listing,
        category: ctx.categoriesDictionary?.[listing.category],
      }),
    ),
  });
};

export const trackToggleContactUs = ({ type }) => {
  return;
  const event = events.TOGGLE_CONTACT_US;
  trackGA4Event({
    event,
    type,
  });
};

export const trackSubmitContactUs = (options) => {
  return;
  const event = events.SUBMIT_CONTACT_US;
  trackGA4Event({
    event,
    ...options,
  });
};

export const trackCloseServicesPopup = () => {
  return;
  const event = events.CLOSE_POPUP_SERVICE;
  trackGA4Event({
    event,
  });
};

export const trackOpenBuildingCalculator = () => {
  return;
  const event = events.OPEN_BUILDING_CALCULATOR;
  trackGA4Event({
    event,
  });
};

export const trackOpenAddFeatured = () => {
  return;
  const event = events.OPEN_ADD_FEATURED;
  trackGA4Event({
    event,
  });
};

export const trackOpenServices = () => {
  return;
  const event = events.OPEN_SERVICES;
  trackGA4Event({
    event,
  });
};

export const trackOpenRentContracts = () => {
  return;
  const event = events.OPEN_RENT_CONTRACTS;
  trackGA4Event({
    event,
  });
};

export const trackOpenStats = () => {
  return;
  const event = events.OPEN_STATS;
  trackGA4Event({
    event,
  });
};

export const trackOpenConstruction = () => {
  return;
  const event = events.OPEN_CONSTRUCTION;
  trackGA4Event({
    event,
  });
};

export const trackOpenReportListingModal = () => {
  return;
  const event = events.OPEN_REPORT_LISTING_MODAL;
  trackGA4Event({
    event,
  });
};

export const trackSubmitListingReport = ({ id, content, reason }) => {
  return;
  const event = events.SUBMIT_LISTING_REPORT;
  trackGA4Event({
    event,
    listing_id: id,
    content,
    reason,
  });
};

export const trackOpenPropertyRegistration = () => {
  return;
  const event = events.OPEN_PROPERTY_REGISTRATION;
  trackGA4Event({
    event,
  });
};

export const trackOpenAlrajhi = () => {
  return;
  const event = events.CLICK_ON_ALRAJHI_FROM_SERVICES;
  trackGA4Event({
    event,
  });
};

export const trackOpenAljazira = () => {
  return;
  const event = events.CLICK_ON_ALJAZIRA_FROM_SERVICES;
  trackGA4Event({
    event,
  });
};

export const trackOpenSurveyingProperty = () => {
  return;
  const event = events.OPEN_SURVEYING_PROPERTY;
  trackGA4Event({
    event,
  });
};

export const trackOpenListingsFee = () => {
  return;
  const event = events.OPEN_LISTING_FEE;
  trackGA4Event({
    event,
  });
};

export const trackDailyRentModePopup = (data = {}) => {
  return;
  const event = events.DAILY_RENT_MODE;

  trackGA4Event({
    event,
    ...data,
  });
};
export const trackRedMode = () => {
  // const event = events.RED_MODE;
  // trackGA4Event({
  //   event,
  // });
};

export const trackDailyRentReserve = (formattedListing) => {
  return;
  const event = events.DAILY_RENT_RESERVE;

  trackGA4Event({
    event,
    ...formattedListing,
  });
};

export const trackDailyRentPayment = (formattedListing) => {
  return;
  const event = events.DAILY_RENT_PAY;
  trackGA4Event({
    event,
    ...formattedListing,
  });
};

export const trackAddNewLicensedListing = () => {
  return;
  const event = events.ADD_NEW_LICENSED_LISTING;

  trackGA4Event({
    event,
  });
};

export const trackAddLicensingRequestByAqar = () => {
  return;
  const event = events.ADD_LICENSING_REQUEST_BY_AQAR;

  trackGA4Event({
    event,
  });
};

export const trackAddMarketingRequest = () => {
  return;
  const event = events.ADD_MARKETING_REQUEST_EVENT;

  trackGA4Event({
    event,
  });
};

export const trackDailyRentMapChangeFilter = () => {
  return;
  const event = events.DAILY_RENT_CHANGE_FILTER;

  trackGA4Event({
    event,
  });
};

export const trackDailyChat = (data) => {
  return;
  const event = events.DAILY_RENT_CHAT;
  trackGA4Event({
    event,
    ...data,
  });
};

export const trackClickSimilarListing = (data = {}) => {
  return;
  const event = events.CLICK_SIMILAR_LISTING;
  trackGA4Event({
    event,
    ...formatListing4GA(data),
  });
};

export const trackViewListing = (data = {}) => {
  return;
  const event = events.VIEW_LISTING;
  trackGA4Event({
    event,
    ...formatListing4GA(data),
  });
};
